// Angular
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
// RxJS
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil, tap } from 'rxjs/operators';

// Auth
import { AuthNoticeService, AuthService } from '../../../../core/auth';
import { ToastrService } from 'ngx-toastr';
import { Title } from '@angular/platform-browser';
//validator
import { ConfirmPasswordValidator } from './confirm-password.validator';
import { MatPasswordStrengthComponent } from '@angular-material-extensions/password-strength';

/**
 * ! Just example => Should be removed in development
 */
const DEMO_PARAMS = {
	EMAIL: 'admin@demo.com',
	PASSWORD: 'demo'
};

@Component({
	selector: 'kt-setpassword',
	templateUrl: './set-password.component.html',
	encapsulation: ViewEncapsulation.None
})
export class SetPasswordComponent implements OnInit, OnDestroy {
	// Public params
	Form: FormGroup;
	loading = false;
	isLoggedIn$: Observable<boolean>;
	errors: any = [];

	private unsubscribe: Subject<any>;

	private returnUrl: any;
	status: any;
	user_token: any;
	userData: any;
	is_submit_enabled: boolean = true;

	@ViewChild('passwordComponentWithConfirmation', {})
	passwordComponentWithConfirmation: MatPasswordStrengthComponent;
	passwordStrength: number;

	// Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

	/**
	 * Component constructor
	 *
	 * @param router: Router
	 * @param auth: AuthService
	 * @param authNoticeService: AuthNoticeService	 
	 * @param store: Store<AppState>
	 * @param fb: FormBuilder
	 * @param cdr
	 * @param route
	 */
	constructor(
		private router: Router,
		private auth: AuthService,
		private authNoticeService: AuthNoticeService,		
		private fb: FormBuilder,
		private cdr: ChangeDetectorRef,
		private route: ActivatedRoute,
		private toastr: ToastrService,
		private titleService: Title
	) {
		this.unsubscribe = new Subject();
		this.titleService.setTitle('Debtor Education | Reset Password');
	}

	/**
	 * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
	 */

	/**
	 * On init
	 */
	ngOnInit(): void {
		this.initForm();
		//this.checkValidToken();

		// redirect back to the returnUrl before login
		let url_string = window.location.href
		let url = new URL(url_string);
		this.user_token = url.searchParams.get("Code");
		//this.user_token = this.route.snapshot.params['token'];
		
		
		this.route.queryParams.subscribe(params => {
			this.returnUrl = params['returnUrl'] || '/auth/login';
		});
	}


	onStrengthChanged(strength: number) {
		this.passwordStrength = strength;
		//console.log('password strength = ', strength);
	}

	checkValidToken() {
		this.user_token = this.route.snapshot.params['token'];
		this.auth.verifyToken(this.user_token)
			.subscribe(res => {
				if (res.status == 1) {
					this.status = res.status;
					this.userData = res.data.data;
				} else {
					this.toastr.error(res.data.data.message, 'Oops!');
					this.router.navigateByUrl(this.returnUrl);
				}
			});

	}

	/**
	 * On destroy
	 */
	ngOnDestroy(): void {
		this.authNoticeService.setNotice(null);
		this.unsubscribe.next();
		this.unsubscribe.complete();
		this.loading = false;
	}

	/**
	 * Form initalization
	 * Default params, validators
	 */
	initForm() {
		this.Form = this.fb.group({
			email: ['', Validators.compose([
				Validators.required,
				Validators.email,
				Validators.minLength(3),
				Validators.maxLength(320) // https://stackoverflow.com/questions/386294/what-is-the-maximum-length-of-a-valid-email-address
			])
			],
			password: ['', Validators.compose([
				Validators.required,
				Validators.minLength(3),
				Validators.maxLength(100),
				Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/)
			])
			],
			confirmPassword: ['', Validators.compose([
				Validators.required,
				Validators.minLength(3),
				Validators.maxLength(100)
			])
			]
		},
			{
				validator: ConfirmPasswordValidator.MatchPassword
			});
	}

	/**
	 * Form Submit
	 */
	submit() {
		this.is_submit_enabled = true;
		const controls = this.Form.controls;
		/** check form */
		if (this.Form.invalid) {
			Object.keys(controls).forEach(controlName =>
				controls[controlName].markAsTouched()
			);
			return;
		}

		this.loading = true;

		const authData = {
			email : controls['email'].value,
			password: controls['password'].value,
			confirmPassword: controls['confirmPassword'].value,
			code: this.user_token
		};
	
		
		this.auth
			.setPassword(authData)
			.pipe(
				tap(),
				takeUntil(this.unsubscribe),
				finalize(() => {
					this.loading = false;
					this.cdr.markForCheck();
				})
			)
			.subscribe(
				data => {
					if (data.succeeded) {
						this.is_submit_enabled = false;
						this.loading = false;
						this.cdr.markForCheck();
						if (data.data != "True") {						
							this.router.navigate(['auth/login']).then(() => {
								this.toastr.success(data.message, 'Success!');								
							});
						} else {
							this.router.navigate(['auth/attorney/login']).then(() => {
								this.toastr.success(data.message, 'Success!');
							});
						}
					} else {
						this.is_submit_enabled = true;
						this.toastr.error(data.message, 'Oops!');
					}
				},
				error => {
					//console.log("err", error)
				}
			);
	}

	/**
	 * Checking control validation
	 *
	 * @param controlName: string => Equals to formControlName
	 * @param validationType: string => Equals to valitors name
	 */
	isControlHasError(controlName: string, validationType: string): boolean {
		const control = this.Form.controls[controlName];
		if (!control) {
			return false;
		}

		const result = control.hasError(validationType) && (control.dirty || control.touched);
		return result;
	}
}
